/**
 * CandidaturesPro — Phase 101ew (Feb 2026)
 *
 * Page admin unifiée qui remplace `/revendeurs/candidatures`. Deux onglets :
 *  1. Candidatures Revendeur (reseller_applications)
 *  2. Candidatures Hôte distributeur (distributor_host_applications) — nouveau !
 *
 * Route : /candidatures-pro (redirect depuis /revendeurs/candidatures)
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Briefcase, Home, Loader2, CheckCircle2, XCircle, UserCheck, Mail, Phone,
  MapPin, Building2, MessageSquare, Clock, Inbox, ArrowRight, Copy,
} from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";

const STATUS_LABELS = {
  pending: { label: "À traiter", bg: "#FBF4E9", text: "#8C6B2F", border: "#E8D3A8" },
  visited: { label: "Visité", bg: "#F5EBFA", text: "#5B3A8C", border: "#D3B8E8" },
  approved: { label: "Approuvée", bg: "#E8F2EC", text: "#1F4E3D", border: "#A8C9B6" },
  rejected: { label: "Refusée", bg: "#FBEEEA", text: "#A04E2C", border: "#E8B0A0" },
  converted: { label: "Convertie", bg: "#E8EEF6", text: "#1F3A66", border: "#A8BBD6" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-CH", { day: "2-digit", month: "short", year: "2-digit" }); }
  catch { return "—"; }
};

function StatusBadge({ status }) {
  const meta = STATUS_LABELS[status] || { label: status, bg: "#F4F4F0", text: "#575A57", border: "#E5E5E0" };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold border"
      style={{ backgroundColor: meta.bg, color: meta.text, borderColor: meta.border }}
    >
      {meta.label}
    </span>
  );
}

export default function CandidaturesPro() {
  const [tab, setTab] = useState("resellers"); // "resellers" | "hosts"
  return (
    <div className="p-6 space-y-5" data-testid="candidatures-pro-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-[#1F4E3D] flex items-center justify-center">
          <Inbox className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-heading font-semibold">Candidatures pro</h1>
          <p className="text-sm text-[#575A57]">Revendeurs B2B + hôtes distributeur — un seul endroit pour suivre les inscriptions</p>
        </div>
      </div>

      <div className="flex items-center gap-2 border-b border-[#E5E5E0]">
        <button
          onClick={() => setTab("resellers")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === "resellers" ? "border-[#8C6B2F] text-[#8C6B2F]" : "border-transparent text-[#575A57] hover:text-[#1A1C1A]"
          }`}
          data-testid="tab-resellers"
        >
          <Briefcase className="w-4 h-4 inline mr-1.5" /> Revendeurs
        </button>
        <button
          onClick={() => setTab("hosts")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === "hosts" ? "border-[#5B3A8C] text-[#5B3A8C]" : "border-transparent text-[#575A57] hover:text-[#1A1C1A]"
          }`}
          data-testid="tab-hosts"
        >
          <Home className="w-4 h-4 inline mr-1.5" /> Hôtes distributeur
        </button>
      </div>

      {tab === "resellers" ? <ResellerAppsTab /> : <HostAppsTab />}
    </div>
  );
}

function ResellerAppsTab() {
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");

  const load = async () => {
    setLoading(true);
    try {
      const url = statusFilter === "ALL" ? "/admin/reseller-applications" : `/admin/reseller-applications?status=${statusFilter}`;
      const { data } = await api.get(url);
      setApps(data.applications || []);
      setStats(data.stats || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const setStatus = async (app, status) => {
    try {
      await api.patch(`/admin/reseller-applications/${app.id}`, { status });
      toast.success(`Candidature marquée ${STATUS_LABELS[status]?.label || status}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const convert = async (app) => {
    if (!window.confirm(`Convertir « ${app.company_name || app.contact_name} » en Revendeur ? Un email d'activation sera envoyé à ${app.email}.`)) return;
    try {
      await api.post(`/admin/reseller-applications/${app.id}/convert`, { discount_percent: 10 });
      toast.success("Revendeur créé + email d'activation envoyé");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2 flex-wrap" data-testid="reseller-apps-filters">
        {["pending", "approved", "rejected", "converted", "ALL"].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
              statusFilter === s ? "bg-[#8C6B2F] text-white border-[#8C6B2F]" : "bg-white border-[#E5E5E0] text-[#575A57]"
            }`}
            data-testid={`filter-status-reseller-${s}`}
          >
            {s === "ALL" ? "Tous" : STATUS_LABELS[s]?.label || s} ({stats[s.toLowerCase()] ?? (s === "ALL" ? apps.length : 0)})
          </button>
        ))}
      </div>
      {loading ? (
        <div className="text-center py-8 text-sm text-[#575A57] flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      ) : apps.length === 0 ? (
        <div className="text-center py-10 text-sm text-[#575A57] italic border border-dashed border-[#E5E5E0] rounded">
          Aucune candidature dans ce filtre.
        </div>
      ) : (
        <div className="space-y-2">
          {apps.map((app) => (
            <div
              key={app.id}
              className="bg-white border border-[#E5E5E0] rounded p-3 hover:border-[#8C6B2F] transition-colors"
              data-testid={`reseller-app-${app.id}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[#1A1C1A]">{app.company_name || app.contact_name}</span>
                    <StatusBadge status={app.status} />
                    <span className="text-[10px] text-[#A3A6A3]">
                      <Clock className="w-3 h-3 inline mr-0.5" /> {fmtDate(app.created_at)}
                    </span>
                  </div>
                  <div className="text-xs text-[#575A57] mt-1 flex items-center gap-3 flex-wrap">
                    {app.email && <span><Mail className="w-3 h-3 inline mr-1" />{app.email}</span>}
                    {app.phone && <span><Phone className="w-3 h-3 inline mr-1" />{app.phone}</span>}
                    {app.city && <span><MapPin className="w-3 h-3 inline mr-1" />{app.postal_code} {app.city}</span>}
                  </div>
                  {app.message && (
                    <p className="text-xs text-[#575A57] mt-2 italic bg-[#FAFAF7] p-2 rounded border-l-2 border-[#E5E5E0]">
                      « {app.message} »
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {app.status === "pending" && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => setStatus(app, "rejected")} className="text-[#C05C47]" data-testid={`reject-${app.id}`}>
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Refuser
                      </Button>
                      <Button size="sm" onClick={() => convert(app)} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid={`convert-${app.id}`}>
                        <UserCheck className="w-3.5 h-3.5 mr-1" /> Convertir
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HostAppsTab() {
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [notesEditing, setNotesEditing] = useState(null); // {id, text}

  const load = async () => {
    setLoading(true);
    try {
      const url = statusFilter === "ALL"
        ? "/admin/distributor-host-applications"
        : `/admin/distributor-host-applications?status=${statusFilter}`;
      const { data } = await api.get(url);
      setApps(data.applications || []);
      setStats(data.stats || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const setStatus = async (app, status) => {
    try {
      await api.patch(`/admin/distributor-host-applications/${app.id}`, { status });
      toast.success(`Marquée ${STATUS_LABELS[status]?.label || status}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const saveNotes = async () => {
    if (!notesEditing) return;
    try {
      await api.patch(`/admin/distributor-host-applications/${notesEditing.id}`, { notes_admin: notesEditing.text });
      toast.success("Note enregistrée");
      setNotesEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const STATUSES = ["pending", "visited", "approved", "rejected", "converted", "ALL"];

  return (
    <div className="space-y-3">
      <div className="flex gap-2 flex-wrap" data-testid="host-apps-filters">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
              statusFilter === s ? "bg-[#5B3A8C] text-white border-[#5B3A8C]" : "bg-white border-[#E5E5E0] text-[#575A57]"
            }`}
            data-testid={`filter-status-host-${s}`}
          >
            {s === "ALL" ? "Toutes" : STATUS_LABELS[s]?.label || s} ({stats[s.toLowerCase()] ?? (s === "ALL" ? apps.length : 0)})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-8 text-sm text-[#575A57] flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      ) : apps.length === 0 ? (
        <div className="text-center py-10 text-sm text-[#575A57] italic border border-dashed border-[#E5E5E0] rounded">
          Aucune candidature hôte dans ce filtre.
        </div>
      ) : (
        <div className="space-y-2">
          {apps.map((app) => (
            <div key={app.id} className="bg-white border border-[#E5E5E0] rounded p-3 hover:border-[#5B3A8C] transition-colors" data-testid={`host-app-${app.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[#1A1C1A]">{app.business_name || app.contact_name}</span>
                    <StatusBadge status={app.status} />
                    <Badge variant="outline" className="text-[9px]">{app.business_type}</Badge>
                    {app.estimated_daily_visitors && (
                      <Badge className="text-[9px] bg-[#F5EBFA] text-[#5B3A8C] border-[#D3B8E8]">
                        {app.estimated_daily_visitors} passages/jour
                      </Badge>
                    )}
                    <span className="text-[10px] text-[#A3A6A3]">
                      <Clock className="w-3 h-3 inline mr-0.5" /> {fmtDate(app.created_at)}
                    </span>
                  </div>
                  <div className="text-xs text-[#575A57] mt-1 flex items-center gap-3 flex-wrap">
                    {app.email && <span><Mail className="w-3 h-3 inline mr-1" />{app.email}</span>}
                    {app.phone && <span><Phone className="w-3 h-3 inline mr-1" />{app.phone}</span>}
                    {app.city && <span><MapPin className="w-3 h-3 inline mr-1" />{app.postal_code} {app.city}</span>}
                  </div>
                  {app.message && (
                    <p className="text-xs text-[#575A57] mt-2 italic bg-[#FAFAF7] p-2 rounded border-l-2 border-[#E5E5E0]">
                      « {app.message} »
                    </p>
                  )}
                  {notesEditing?.id === app.id ? (
                    <div className="mt-2 flex gap-2">
                      <Input
                        value={notesEditing.text}
                        onChange={(e) => setNotesEditing({ ...notesEditing, text: e.target.value })}
                        placeholder="Note admin (visite prévue, refus, …)"
                        className="text-xs"
                        data-testid={`notes-input-${app.id}`}
                      />
                      <Button size="sm" onClick={saveNotes} data-testid={`save-notes-${app.id}`}>OK</Button>
                      <Button size="sm" variant="ghost" onClick={() => setNotesEditing(null)}>Annuler</Button>
                    </div>
                  ) : app.notes_admin ? (
                    <p onClick={() => setNotesEditing({ id: app.id, text: app.notes_admin })}
                       className="text-[11px] text-[#5B3A8C] mt-2 bg-[#F5EBFA] p-1.5 rounded cursor-text hover:bg-[#EEDDF8]"
                       title="Clic pour éditer">
                      <MessageSquare className="w-3 h-3 inline mr-1" /> {app.notes_admin}
                    </p>
                  ) : null}
                </div>
                <div className="flex items-center gap-1 shrink-0 flex-wrap">
                  {!notesEditing || notesEditing.id !== app.id ? (
                    <Button size="sm" variant="ghost" onClick={() => setNotesEditing({ id: app.id, text: app.notes_admin || "" })} className="h-7 px-2" data-testid={`add-notes-${app.id}`}>
                      <MessageSquare className="w-3.5 h-3.5" />
                    </Button>
                  ) : null}
                  {app.status === "pending" && (
                    <Button size="sm" variant="outline" onClick={() => setStatus(app, "visited")} className="text-[#5B3A8C]" data-testid={`visit-${app.id}`}>
                      Marquer visité
                    </Button>
                  )}
                  {(app.status === "pending" || app.status === "visited") && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => setStatus(app, "rejected")} className="text-[#C05C47]" data-testid={`reject-host-${app.id}`}>
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Refuser
                      </Button>
                      <Button size="sm" onClick={() => setStatus(app, "approved")} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid={`approve-host-${app.id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approuver
                      </Button>
                    </>
                  )}
                  {app.status === "approved" && (
                    <Button size="sm" onClick={() => setStatus(app, "converted")} className="bg-[#8C6B2F] hover:bg-[#735524]" data-testid={`convert-host-${app.id}`}>
                      <ArrowRight className="w-3.5 h-3.5 mr-1" /> Marquer converti
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="text-[10px] text-[#A3A6A3] italic mt-4">
        Note : la conversion d'une candidature hôte en Distributeur se fait manuellement — créez le distributeur dans /distributeurs
        puis passez cette candidature en « Converti » pour la classer.
      </p>
    </div>
  );
}
